import { useQuery, useQueryClient } from '@tanstack/react-query'

import { BarraProgresso, Cartao, Esqueleto, ItemCheck, TituloSecao } from '@/componentes/ui'
import { formatarData } from '@/lib/datas'
import { CHAVES, type ContextoPrograma, usePerfil } from '@/lib/sessao'
import { supabase } from '@/lib/supabase'
import { primeiroNome, saudacao } from '@/lib/utilitarios'
import { PILARES, ROTULO_PILAR, type Pilar } from '@/tipos/dominio'
import type { DiaPrograma, Missao } from '@/tipos/tabelas'

/**
 * Tela inicial (seção 4.1).
 *
 * O que ela precisa entregar em menos de um segundo: em que dia estou,
 * quanto já andei e o que tenho para fazer hoje. Tudo que exige rede
 * degrada em esqueleto, nunca em tela branca — ela abre isto às 6h da
 * manhã, muitas vezes com sinal ruim.
 */
export function PaginaInicio({
  contexto,
  aoAbrirPilar,
}: {
  contexto: ContextoPrograma
  aoAbrirPilar: (pilar: Pilar) => void
}) {
  const { data: perfil } = usePerfil()
  const { dia, percentual, duracaoDias, matricula } = contexto

  const { data: diaPrograma, isLoading } = useQuery({
    queryKey: CHAVES.diaPrograma(dia),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dias_programa')
        .select('*, missoes(*)')
        .eq('programa_id', matricula.turma.programa.id)
        .eq('numero', dia)
        .maybeSingle()

      if (error) throw error
      return data as (DiaPrograma & { missoes: Missao[] }) | null
    },
    staleTime: 30 * 60 * 1000,
  })

  // Liberação por data (seção 14): a administradora pode segurar um dia
  // mesmo que a participante já tenha chegado nele.
  const bloqueadoAte =
    diaPrograma?.liberado_em && new Date(diaPrograma.liberado_em) > new Date()
      ? new Date(diaPrograma.liberado_em)
      : null

  const missoes = bloqueadoAte
    ? []
    : [...(diaPrograma?.missoes ?? [])].sort((a, b) => a.ordem - b.ordem)

  return (
    <div className="flex flex-col gap-6 px-5 pt-6 pb-28">
      <header>
        <h1 className="font-titulo text-2xl leading-tight font-bold">
          {saudacao()}
          {perfil?.nome ? `, ${primeiroNome(perfil.nome)}` : ''}.
        </h1>
        <p className="mt-1 text-[15px] text-[var(--tinta-suave)]">
          Hoje é o <strong className="text-coral">Dia {dia}</strong> do Projeto Viver.
        </p>
        {diaPrograma?.frase_inspiradora && (
          <p className="mt-3 border-l-3 border-dourado pl-3 text-[15px] leading-relaxed italic">
            {diaPrograma.frase_inspiradora}
          </p>
        )}
      </header>

      <section aria-label="Seu progresso">
        <div className="mb-2 flex items-baseline justify-between">
          <span className="font-titulo text-sm font-semibold text-[var(--tinta-suave)]">
            Dia {dia} de {duracaoDias}
          </span>
          <span className="font-titulo text-sm font-bold text-coral">{percentual}%</span>
        </div>
        <BarraProgresso valor={percentual} rotulo={`Dia ${dia} de ${duracaoDias}`} />
      </section>

      <section aria-label="Pilares">
        <div className="grid grid-cols-5 gap-2">
          {PILARES.map((pilar) => (
            <button
              key={pilar}
              type="button"
              onClick={() => aoAbrirPilar(pilar)}
              className="flex flex-col items-center gap-1 rounded-2xl bg-[var(--cartao)] px-1 py-3 shadow-[var(--shadow-suave)] transition-transform active:scale-95"
            >
              <span className="text-2xl" aria-hidden>
                {ROTULO_PILAR[pilar].emoji}
              </span>
              <span className="text-[11px] leading-tight font-medium text-[var(--tinta-suave)]">
                {ROTULO_PILAR[pilar].nome}
              </span>
            </button>
          ))}
        </div>
      </section>

      <section aria-label="Missão do dia">
        <TituloSecao>Missão do dia</TituloSecao>

        {isLoading ? (
          <div className="flex flex-col gap-2">
            {[0, 1, 2, 3, 4].map((n) => (
              <Esqueleto key={n} className="h-16" />
            ))}
          </div>
        ) : bloqueadoAte ? (
          <Cartao className="bg-dourado/15 text-center">
            <span className="text-3xl" aria-hidden>
              🔒
            </span>
            <p className="mt-2 font-titulo text-[15px] font-semibold">
              A missão de hoje abre em {formatarData(bloqueadoAte, "d/MM 'às' HH:mm")}
            </p>
            <p className="mt-1 text-sm text-[var(--tinta-suave)]">
              Enquanto isso, você pode revisar a biblioteca e preencher o diário de ontem.
            </p>
          </Cartao>
        ) : missoes.length === 0 ? (
          <Cartao className="text-sm text-[var(--tinta-apagada)]">
            A missão de hoje ainda não foi publicada.
          </Cartao>
        ) : (
          <ListaMissoes missoes={missoes} matriculaId={matricula.id} dia={dia} />
        )}
      </section>
    </div>
  )
}

function ListaMissoes({
  missoes,
  matriculaId,
  dia,
}: {
  missoes: Missao[]
  matriculaId: string
  dia: number
}) {
  const clienteQuery = useQueryClient()

  const { data: concluidas = new Set<string>() } = useQuery({
    queryKey: ['missoes-concluidas', matriculaId, dia],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('missoes_concluidas')
        .select('missao_id')
        .eq('matricula_id', matriculaId)
        .in(
          'missao_id',
          missoes.map((m) => m.id),
        )

      if (error) throw error
      return new Set((data as { missao_id: string }[]).map((linha) => linha.missao_id))
    },
  })

  async function alternar(missaoId: string) {
    const jaFeita = concluidas.has(missaoId)

    // Atualização otimista: marcar um item da missão precisa responder
    // no toque, não depois da ida ao servidor.
    clienteQuery.setQueryData(['missoes-concluidas', matriculaId, dia], (atual: Set<string>) => {
      const proximo = new Set(atual)
      if (jaFeita) proximo.delete(missaoId)
      else proximo.add(missaoId)
      return proximo
    })

    if (jaFeita) {
      await supabase
        .from('missoes_concluidas')
        .delete()
        .eq('matricula_id', matriculaId)
        .eq('missao_id', missaoId)
    } else {
      await supabase
        .from('missoes_concluidas')
        .upsert({ matricula_id: matriculaId, missao_id: missaoId })
    }
  }

  const total = missoes.length
  const feitas = missoes.filter((m) => concluidas.has(m.id)).length

  return (
    <div className="flex flex-col gap-2">
      {feitas > 0 && (
        <p className="text-xs font-medium text-verde">
          {feitas} de {total} concluídas
          {feitas === total && ' — dia completo! 🎉'}
        </p>
      )}

      {missoes.map((missao) => (
        <ItemCheck
          key={missao.id}
          titulo={missao.titulo}
          descricao={missao.descricao}
          emoji={missao.pilar ? ROTULO_PILAR[missao.pilar].emoji : undefined}
          marcado={concluidas.has(missao.id)}
          aoAlternar={() => void alternar(missao.id)}
        />
      ))}
    </div>
  )
}
